import {
  CellValues,
  ColumnHeading,
  CustomSortKeyFnMap,
  EntityFilterControlsConfig,
  MultiSelectOption,
  PaginatedDataTable,
  TableCellStylesFn,
  useIsDesktop,
  useVisibleHeadings,
} from '@lbcamden/lib-react';
import { formatDMY, formatOrFallback } from '@lbcamden/shared-utils';
import { FunctionComponent, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';

import { GlobalStore } from '~/api/tinybase/GlobalStore';
import { SectionContainer } from '~/client/components/common/SectionContainer';
import {
  useHousingApplicationList,
  useReferenceListDisplayValue,
  useReferenceListSelectOptions,
  useUserStore,
} from '~/client/hooks/storeHooks';
import { toHousingApplicationPage } from '~/client/utils/navigation';
import { FullHousingApplication, ScoCodes } from '~/common/types/housingApplications';
import { Person } from '~/common/types/people';
import { getPersonNameSortKey } from '~/common/utils/sort';

type Props = {
  propertyId: number;
};

type ColumnId = StringKey<FullHousingApplication>;

const headings = [
  { id: 'applicantPersonIds', content: 'Applicants' },
  { id: 'receivedDate', content: 'Date' },
  { id: 'status', content: 'Status' },
  { id: 'points', content: 'Points' },
] as const satisfies ColumnHeading<ColumnId>[];

const getCustomSortKeyFnMap = (
  getPersonFromId: (personId: number) => Person | undefined,
): CustomSortKeyFnMap<FullHousingApplication, ColumnId> => ({
  applicantPersonIds: ({ applicantPersonIds }) => {
    const people = applicantPersonIds.map((personId) => getPersonFromId(personId)!).filter(Boolean);
    return people.map((person) => getPersonNameSortKey(person)).join();
  },
});

const getFilterConfigs = (
  personOptions: MultiSelectOption<number>[],
  statusOptions: MultiSelectOption[],
): EntityFilterControlsConfig<FullHousingApplication> => [
  {
    type: 'multi-select',
    key: 'applicantPersonIds',
    label: 'Applicant',
    options: personOptions,
  },
  {
    type: 'multi-select',
    key: 'status',
    label: 'Status',
    options: statusOptions,
  },
];

const getMapApplicationToRow =
  (
    getPersonFromId: (personId: number) => Person | undefined,
    refListDisplayValue: GlobalStore['referenceListDisplayValue'],
  ) =>
  (application: FullHousingApplication): CellValues<ColumnId> => ({
    _rowId: String(application.applicationId),
    applicantPersonIds: application.applicantPersonIds
      .map((personId) => getPersonFromId(personId)?.fullName)
      .join(', '),
    receivedDate: formatOrFallback(application.receivedDate, formatDMY),
    status: refListDisplayValue('STATUS_CODE', application.status),
    points: application.points,
  });

const getCellStyles =
  (isDesktop?: boolean): TableCellStylesFn =>
  (columnId) => [
    { width: 50 },
    columnId === 'applicantPersonIds' && { width: isDesktop ? 250 : 150 },
  ];

export const HousingApplications: FunctionComponent<Props> = ({ propertyId }) => {
  const navigate = useNavigate();

  const isDesktop = useIsDesktop();

  const userStore = useUserStore();
  const applications = useHousingApplicationList(propertyId);
  const referenceListDisplayValue = useReferenceListDisplayValue();
  const referenceListSelectOptions = useReferenceListSelectOptions();

  const getPersonFromId = useCallback(
    (personId: number) => userStore?.get('people', personId) as Person,
    [userStore],
  );

  const personOptions = useMemo<MultiSelectOption<number>[]>(() => {
    const peopleWithApplications = [
      ...new Set(applications.map((app) => app.applicantPersonIds).flat()),
    ]
      .map(getPersonFromId)
      .filter(Boolean) as Person[];

    return peopleWithApplications.map(({ personId, fullName }) => ({
      id: String(personId),
      label: fullName,
      value: personId,
    }));
  }, [applications]);

  const visibleHeadings = useVisibleHeadings(headings, {
    xs: headings.slice(0, 3),
    sm: headings.slice(0, 3),
  });

  const customSortKeyFnMap = useMemo(
    () => getCustomSortKeyFnMap(getPersonFromId),
    [getPersonFromId],
  );

  const filterConfigs = useMemo(
    () =>
      getFilterConfigs(
        personOptions,
        referenceListSelectOptions('STATUS_CODE').filter(({ value }) => ScoCodes.includes(value)),
      ),
    [personOptions, referenceListSelectOptions],
  );

  const mapApplicationToRow = useMemo(
    () => getMapApplicationToRow(getPersonFromId, referenceListDisplayValue),
    [getPersonFromId],
  );

  const onClickRow = ({ applicationId }: FullHousingApplication) =>
    navigate(toHousingApplicationPage(propertyId, applicationId));

  return (
    <SectionContainer title="Applications and allocations">
      <PaginatedDataTable
        cellStyles={getCellStyles(isDesktop)}
        headings={visibleHeadings}
        customSortKeyFnMap={customSortKeyFnMap}
        filterConfigs={filterConfigs}
        items={applications}
        defaultSort={{ field: 'receivedDate', direction: 'desc' }}
        mapItemToRow={mapApplicationToRow}
        onClickRow={onClickRow}
      />
    </SectionContainer>
  );
};
